import {
  VideoClient,
  VideoClientConfig,
  VideoClientEvent,
} from '../types/video-provider'

/**
 * Vanilla WebRTC Client (Frontend)
 * 
 * Frontend implementation for vanilla WebRTC using browser APIs.
 * Handles peer connections, media management, and WebRTC signaling.
 * 
 * This client uses WebSocket signaling for peer-to-peer connections.
 */
export class VanillaWebRTCClient implements VideoClient {
  readonly name = 'vanilla-webrtc'
  private config?: VideoClientConfig
  private initialized = false
  private peerConnection?: RTCPeerConnection
  private eventHandlers: Map<VideoClientEvent, Set<Function>> = new Map()
  private localStream?: MediaStream
  private remoteStream?: MediaStream

  async initialize(config: VideoClientConfig): Promise<void> {
    this.config = config
    this.initialized = true
    console.log('[VanillaWebRTCClient] Initialized')
  }

  /**
   * Join a session
   * For vanilla WebRTC, credentials are not used (uses WebSocket signaling)
   */
  async joinSession(sessionId: string, credentials?: any): Promise<void> {
    if (!this.initialized) {
      throw new Error('VanillaWebRTCClient not initialized')
    }

    console.log('[VanillaWebRTCClient] Joined session:', sessionId)
    // Actual connection happens via WebRTC signaling (offer/answer)
    // This is just a logical "join" - no authentication needed
  }

  /**
   * Leave the current session
   */
  async leaveSession(): Promise<void> {
    if (this.peerConnection) {
      this.peerConnection.close()
      this.peerConnection = undefined
    }
    
    this.localStream?.getTracks().forEach(track => track.stop())
    this.localStream = undefined
    this.remoteStream = undefined

    console.log('[VanillaWebRTCClient] Left session')
    this.emitEvent('disconnected')
  }

  /**
   * Get local media stream (camera + microphone)
   */
  async getLocalMedia(constraints?: MediaStreamConstraints): Promise<MediaStream> {
    const defaultConstraints: MediaStreamConstraints = {
      video: {
        facingMode: 'user',
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
      },
    }

    try {
      this.localStream = await navigator.mediaDevices.getUserMedia(
        constraints || defaultConstraints
      )
      console.log('[VanillaWebRTCClient] Got local media')
      return this.localStream
    } catch (error) {
      console.error('[VanillaWebRTCClient] Failed to get local media:', error)
      this.emitEvent('error', error)
      throw error
    }
  }

  /**
   * Start local video
   */
  async startVideo(): Promise<void> {
    const videoTrack = this.localStream?.getVideoTracks()[0]
    if (videoTrack) {
      videoTrack.enabled = true
      console.log('[VanillaWebRTCClient] Video started')
    }
  }

  /**
   * Stop local video
   */
  async stopVideo(): Promise<void> {
    const videoTrack = this.localStream?.getVideoTracks()[0]
    if (videoTrack) {
      videoTrack.enabled = false
      console.log('[VanillaWebRTCClient] Video stopped')
    }
  }

  /**
   * Mute local audio
   */
  async muteAudio(): Promise<void> {
    const audioTrack = this.localStream?.getAudioTracks()[0]
    if (audioTrack) {
      audioTrack.enabled = false
      console.log('[VanillaWebRTCClient] Audio muted')
    }
  }

  /**
   * Unmute local audio
   */
  async unmuteAudio(): Promise<void> {
    const audioTrack = this.localStream?.getAudioTracks()[0]
    if (audioTrack) {
      audioTrack.enabled = true
      console.log('[VanillaWebRTCClient] Audio unmuted')
    }
  }

  /**
   * Get remote media streams
   */
  getRemoteStreams(): MediaStream[] {
    return this.remoteStream ? [this.remoteStream] : []
  }

  // ==============================
  // Closed-Captioning Methods (Not Supported in Phase 1)
  // ==============================

  /**
   * Enable closed captions
   * NOT SUPPORTED in vanilla WebRTC for Phase 1
   * Phase 2+ will integrate AWS Transcribe Medical or similar service
   */
  async enableClosedCaptions(language: string): Promise<void> {
    throw new Error('Closed captions not supported for vanilla WebRTC in Phase 1')
  }

  /**
   * Disable closed captions
   * NOT SUPPORTED in vanilla WebRTC for Phase 1
   */
  async disableClosedCaptions(): Promise<void> {
    throw new Error('Closed captions not supported for vanilla WebRTC in Phase 1')
  }

  /**
   * Check if closed captions are supported
   * Returns false for vanilla WebRTC in Phase 1
   */
  supportsClosedCaptions(): boolean {
    return false
  }

  /**
   * Get available caption languages
   * Returns empty array for vanilla WebRTC in Phase 1
   */
  getAvailableCaptionLanguages(): string[] {
    return []
  }

  /**
   * Initialize peer connection
   * Used internally by composables
   */
  async initializePeerConnection(config?: RTCConfiguration): Promise<RTCPeerConnection> {
    const iceServers: RTCIceServer[] = []

    // Add STUN servers
    if (this.config?.stunServers && this.config.stunServers.length > 0) {
      this.config.stunServers.forEach(stunServer => {
        iceServers.push({ urls: stunServer })
      })
    }

    // Add TURN servers
    if (this.config?.turnServers && this.config.turnServers.length > 0) {
      this.config.turnServers.forEach(turnServer => {
        iceServers.push({
          urls: turnServer.urls,
          username: turnServer.username,
          credential: turnServer.credential,
        })
      })
    }

    const rtcConfig: RTCConfiguration = {
      ...config,
      iceServers: config?.iceServers || iceServers,
    }

    this.peerConnection = new RTCPeerConnection(rtcConfig)

    // Handle connection state changes
    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection?.connectionState
      console.log('[VanillaWebRTCClient] Connection state:', state)
      
      if (state === 'connected') {
        this.emitEvent('connected')
      } else if (state === 'disconnected' || state === 'failed' || state === 'closed') {
        this.emitEvent('disconnected')
      }
    }

    // Handle remote stream
    this.peerConnection.ontrack = (event) => {
      console.log('[VanillaWebRTCClient] Received remote track', {
        kind: event.track.kind,
        streams: event.streams.length,
        stream: event.streams[0]?.id
      })
      this.remoteStream = event.streams[0]
      console.log('[VanillaWebRTCClient] Emitting remote-stream-added event')
      this.emitEvent('remote-stream-added', this.remoteStream)
      console.log('[VanillaWebRTCClient] Event emitted, listeners:', this.eventHandlers.get('remote-stream-added')?.size || 0)
    }

    return this.peerConnection
  }

  /**
   * Get current peer connection
   */
  getPeerConnection(): RTCPeerConnection | undefined {
    return this.peerConnection
  }

  /**
   * Register event listener
   */
  on(event: VideoClientEvent, handler: (...args: any[]) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set())
    }
    this.eventHandlers.get(event)!.add(handler)
  }

  /**
   * Unregister event listener
   */
  off(event: VideoClientEvent, handler: (...args: any[]) => void): void {
    const handlers = this.eventHandlers.get(event)
    if (handlers) {
      handlers.delete(handler)
    }
  }

  /**
   * Clean up resources
   */
  async dispose(): Promise<void> {
    await this.leaveSession()
    
    this.initialized = false
    this.config = undefined
    this.eventHandlers.clear()
    
    console.log('[VanillaWebRTCClient] Disposed')
  }

  // Private helper methods

  private emitEvent(event: VideoClientEvent, ...args: any[]): void {
    const handlers = this.eventHandlers.get(event)
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(...args)
        } catch (error) {
          console.error(`[VanillaWebRTCClient] Error in event handler for ${event}:`, error)
        }
      })
    }
  }
}
